import type { CommandSet } from "./model/command-set";
import { commandSetContains } from "./model/command-set";
import type { Keystroke } from "./model/keystroke";
import type {
  FindCharVariant,
  OperatorKey,
  PendingCommand,
  TextObjectPrefix,
} from "./model/pending-command";
import { EMPTY_PENDING } from "./model/pending-command";
import type { MotionName } from "./motions";
import { isSingleKeyMotion } from "./motions";
import type { TextObjectName } from "./textobjects";
import { resolveTextObjectName, TEXT_OBJECTS } from "./textobjects";

export interface MotionCommand {
  readonly kind: "motion";
  readonly motion: MotionName;
  readonly count: number | null;
  readonly argument?: string;
}

export interface OperatorMotionTarget {
  readonly kind: "motion";
  readonly motion: MotionName;
  readonly count: number | null;
  readonly argument?: string;
}

export interface OperatorSelfLinesTarget {
  readonly kind: "self-lines";
  readonly count: number | null;
}

export interface OperatorTextObjectTarget {
  readonly kind: "text-object";
  readonly name: TextObjectName;
  readonly count: number | null;
}

export type OperatorTarget =
  | OperatorMotionTarget
  | OperatorSelfLinesTarget
  | OperatorTextObjectTarget;

export interface OperatorCommand {
  readonly kind: "operator";
  readonly operator: OperatorKey;
  readonly target: OperatorTarget;
  readonly register: string | null;
}

export type InsertEntryKey = "i" | "a" | "o" | "I" | "A" | "O";

export interface EnterInsertCommand {
  readonly kind: "enter-insert";
  readonly entry: InsertEntryKey;
}

export interface SimpleEditCommand {
  readonly kind: "simple-edit";
  readonly name: "x";
  readonly count: number | null;
}

export interface HistoryCommand {
  readonly kind: "history";
  readonly op: "undo" | "redo";
}

export interface RepeatCommand {
  readonly kind: "repeat";
}

export interface EnterCommandLineCommand {
  readonly kind: "enter-command-line";
  readonly prefix: "/" | "?" | ":";
}

export interface SearchRepeatCommand {
  readonly kind: "search-repeat";
  /** trueなら直前の検索と逆方向（`N`）。 */
  readonly reversed: boolean;
}

export interface SearchWordCommand {
  readonly kind: "search-word";
  readonly direction: "forward" | "backward";
}

export interface RepeatFindCommand {
  readonly kind: "repeat-find";
  /** trueなら直前のf/tと逆方向（`,`）。 */
  readonly reversed: boolean;
}

export interface PutCommand {
  readonly kind: "put";
  readonly before: boolean;
  readonly register: string | null;
}

export type VisualVariant = "visual" | "visual-line" | "visual-block";

export interface EnterVisualCommand {
  readonly kind: "enter-visual";
  readonly variant: VisualVariant;
}

export interface SetMarkCommand {
  readonly kind: "set-mark";
  readonly name: string;
}

export interface JumpToMarkCommand {
  readonly kind: "jump-to-mark";
  readonly name: string;
}

export interface StartMacroRecordingCommand {
  readonly kind: "start-macro-recording";
  readonly name: string;
}

export interface PlayMacroCommand {
  readonly kind: "play-macro";
  readonly name: string;
  readonly count: number | null;
}

export type Command =
  | MotionCommand
  | OperatorCommand
  | EnterInsertCommand
  | SimpleEditCommand
  | HistoryCommand
  | RepeatCommand
  | EnterCommandLineCommand
  | SearchRepeatCommand
  | SearchWordCommand
  | RepeatFindCommand
  | PutCommand
  | EnterVisualCommand
  | SetMarkCommand
  | JumpToMarkCommand
  | StartMacroRecordingCommand
  | PlayMacroCommand;

export type InterpretResult =
  | { readonly type: "unlearned"; readonly pending: PendingCommand; readonly statusMessage: string }
  | { readonly type: "pending"; readonly pending: PendingCommand }
  | {
      readonly type: "command";
      readonly command: Command;
      readonly pending: PendingCommand;
      /** 直前のpending.rawKeysにこの確定キーを加えた完全な生キー列（ドットリピート用）。 */
      readonly rawKeys: readonly Keystroke[];
    };

export const UNLEARNED_KEY_MESSAGE = "そのコマンドはまだ習っていないよ";
const INSERT_ENTRY_KEYS: ReadonlySet<string> = new Set(["i", "a", "o", "I", "A", "O"]);
const FIND_CHAR_KEYS: ReadonlySet<string> = new Set(["f", "t"]);
const REGISTER_NAME_PATTERN = /^[a-z]$/;

function isCountDigit(key: string, hasCountStarted: boolean): boolean {
  if (/^[1-9]$/.test(key)) {
    return true;
  }
  return key === "0" && hasCountStarted;
}

function combineCounts(a: number | null, b: number | null): number | null {
  if (a === null && b === null) {
    return null;
  }
  return (a ?? 1) * (b ?? 1);
}

/** `i`/`a` の時点で、そのprefixのテキストオブジェクトが1つでも習得済みかを判定する。 */
function hasAnyTextObjectFor(commandSet: CommandSet, prefix: TextObjectPrefix): boolean {
  for (const name of TEXT_OBJECTS.keys()) {
    if (name.startsWith(prefix) && commandSetContains(commandSet, name)) {
      return true;
    }
  }
  return false;
}

function unlearned(): InterpretResult {
  return { type: "unlearned", pending: EMPTY_PENDING, statusMessage: UNLEARNED_KEY_MESSAGE };
}

function confirmOperator(
  pending: PendingCommand,
  target: OperatorTarget,
  rawKeys: readonly Keystroke[],
): InterpretResult {
  const combinedTarget: OperatorTarget = {
    ...target,
    count: combineCounts(pending.operatorCount, target.count),
  } as OperatorTarget;
  return {
    type: "command",
    command: {
      kind: "operator",
      operator: pending.operator as OperatorKey,
      target: combinedTarget,
      register: pending.register,
    },
    pending: EMPTY_PENDING,
    rawKeys,
  };
}

function confirmMotionOrOperator(
  pending: PendingCommand,
  motion: MotionName,
  count: number | null,
  argument: string | undefined,
  rawKeys: readonly Keystroke[],
): InterpretResult {
  // `argument`はexactOptionalPropertyTypesの都合上、値がある場合のみキーを持たせる。
  const motionTarget: OperatorMotionTarget =
    argument === undefined
      ? { kind: "motion", motion, count }
      : { kind: "motion", motion, count, argument };

  if (pending.operator !== null) {
    return confirmOperator(pending, motionTarget, rawKeys);
  }
  return {
    type: "command",
    command: motionTarget,
    pending: EMPTY_PENDING,
    rawKeys,
  };
}

/**
 * 1打分のキー入力を解釈する（ノーマルモード相当）。
 * CommandSetに含まれないキーは「未習得キー」として案内を返し、状態を変えない（F-003）。
 *
 * スコープの簡略化:
 * - 検索（`/` `?` `n` `N` `*` `#`）と行内検索の繰り返し（`;` `,`）はオペレータと組み合わせない
 * - テキストオブジェクト（`iw` 等）はオペレータ経由でのみ使える（本家vimと同じ。単独の`i`/`a`は常に挿入モード）
 * - マーク（`m` `` ` ``）はオペレータと組み合わせない
 */
export function interpret(
  pending: PendingCommand,
  keystroke: Keystroke,
  commandSet: CommandSet,
): InterpretResult {
  const key = keystroke.key;

  if (pending.awaiting === "find-char") {
    const rawKeys = [...pending.rawKeys, keystroke];
    const variant = pending.findVariant as FindCharVariant;
    return confirmMotionOrOperator(pending, variant, pending.count, key, rawKeys);
  }

  if (pending.awaiting === "text-object") {
    const rawKeys = [...pending.rawKeys, keystroke];
    const prefix = pending.textObjectPrefix as TextObjectPrefix;
    const name = resolveTextObjectName(prefix, key);
    if (name === null || !commandSetContains(commandSet, name)) {
      return unlearned();
    }
    return confirmOperator(pending, { kind: "text-object", name, count: pending.count }, rawKeys);
  }

  if (pending.awaiting === "register-name") {
    if (!REGISTER_NAME_PATTERN.test(key)) {
      return unlearned();
    }
    return {
      type: "pending",
      pending: {
        ...pending,
        awaiting: null,
        register: key,
        rawKeys: [...pending.rawKeys, keystroke],
      },
    };
  }

  if (pending.awaiting === "mark-set") {
    if (!REGISTER_NAME_PATTERN.test(key)) {
      return unlearned();
    }
    return {
      type: "command",
      command: { kind: "set-mark", name: key },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (pending.awaiting === "mark-jump") {
    if (!REGISTER_NAME_PATTERN.test(key)) {
      return unlearned();
    }
    return {
      type: "command",
      command: { kind: "jump-to-mark", name: key },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (pending.awaiting === "macro-record-name") {
    if (!REGISTER_NAME_PATTERN.test(key)) {
      return unlearned();
    }
    return {
      type: "command",
      command: { kind: "start-macro-recording", name: key },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (pending.awaiting === "macro-play-name") {
    if (!REGISTER_NAME_PATTERN.test(key)) {
      return unlearned();
    }
    return {
      type: "command",
      command: { kind: "play-macro", name: key, count: pending.count },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (
    key === '"' &&
    pending.awaiting === null &&
    pending.operator === null &&
    pending.register === null &&
    commandSetContains(commandSet, '"')
  ) {
    return {
      type: "pending",
      pending: { ...pending, awaiting: "register-name", rawKeys: [...pending.rawKeys, keystroke] },
    };
  }

  if (pending.awaiting === null && isCountDigit(key, pending.count !== null)) {
    const digit = Number(key);
    const nextCount = (pending.count ?? 0) * 10 + digit;
    return {
      type: "pending",
      pending: { ...pending, count: nextCount, rawKeys: [...pending.rawKeys, keystroke] },
    };
  }

  if (pending.awaiting === "g") {
    if (key === "g" && commandSetContains(commandSet, "gg")) {
      const rawKeys = [...pending.rawKeys, keystroke];
      return confirmMotionOrOperator(pending, "gg", pending.count, undefined, rawKeys);
    }
    return unlearned();
  }

  if (pending.operator !== null) {
    if (key === pending.operator) {
      const rawKeys = [...pending.rawKeys, keystroke];
      return confirmOperator(pending, { kind: "self-lines", count: pending.count }, rawKeys);
    }
    if (key === "g") {
      if (commandSetContains(commandSet, "gg")) {
        return {
          type: "pending",
          pending: { ...pending, awaiting: "g", rawKeys: [...pending.rawKeys, keystroke] },
        };
      }
      return unlearned();
    }
    if ((key === "i" || key === "a") && hasAnyTextObjectFor(commandSet, key)) {
      return {
        type: "pending",
        pending: {
          ...pending,
          awaiting: "text-object",
          textObjectPrefix: key,
          rawKeys: [...pending.rawKeys, keystroke],
        },
      };
    }
    if (FIND_CHAR_KEYS.has(key) && commandSetContains(commandSet, key)) {
      return {
        type: "pending",
        pending: {
          ...pending,
          awaiting: "find-char",
          findVariant: key as FindCharVariant,
          rawKeys: [...pending.rawKeys, keystroke],
        },
      };
    }
    if (isSingleKeyMotion(key) && commandSetContains(commandSet, key)) {
      const rawKeys = [...pending.rawKeys, keystroke];
      return confirmOperator(
        pending,
        { kind: "motion", motion: key, count: pending.count },
        rawKeys,
      );
    }
    return unlearned();
  }

  if ((key === "d" || key === "c" || key === "y") && commandSetContains(commandSet, key)) {
    return {
      type: "pending",
      pending: {
        ...pending,
        operator: key,
        operatorCount: pending.count,
        count: null,
        rawKeys: [...pending.rawKeys, keystroke],
      },
    };
  }

  if (key === "x" && commandSetContains(commandSet, "x")) {
    return {
      type: "command",
      command: { kind: "simple-edit", name: "x", count: pending.count },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (INSERT_ENTRY_KEYS.has(key) && commandSetContains(commandSet, key)) {
    return {
      type: "command",
      command: { kind: "enter-insert", entry: key as InsertEntryKey },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (key === "u" && commandSetContains(commandSet, "u")) {
    return {
      type: "command",
      command: { kind: "history", op: "undo" },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (keystroke.ctrl && key === "r" && commandSetContains(commandSet, "<C-r>")) {
    return {
      type: "command",
      command: { kind: "history", op: "redo" },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (key === "." && commandSetContains(commandSet, ".")) {
    return {
      type: "command",
      command: { kind: "repeat" },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if ((key === "/" || key === "?" || key === ":") && commandSetContains(commandSet, key)) {
    return {
      type: "command",
      command: { kind: "enter-command-line", prefix: key },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (key === "n" && commandSetContains(commandSet, "n")) {
    return {
      type: "command",
      command: { kind: "search-repeat", reversed: false },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (key === "N" && commandSetContains(commandSet, "N")) {
    return {
      type: "command",
      command: { kind: "search-repeat", reversed: true },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (key === "*" && commandSetContains(commandSet, "*")) {
    return {
      type: "command",
      command: { kind: "search-word", direction: "forward" },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (key === "#" && commandSetContains(commandSet, "#")) {
    return {
      type: "command",
      command: { kind: "search-word", direction: "backward" },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (FIND_CHAR_KEYS.has(key) && commandSetContains(commandSet, key)) {
    return {
      type: "pending",
      pending: {
        ...pending,
        awaiting: "find-char",
        findVariant: key as FindCharVariant,
        rawKeys: [...pending.rawKeys, keystroke],
      },
    };
  }

  if (key === ";" && commandSetContains(commandSet, ";")) {
    return {
      type: "command",
      command: { kind: "repeat-find", reversed: false },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (key === "," && commandSetContains(commandSet, ",")) {
    return {
      type: "command",
      command: { kind: "repeat-find", reversed: true },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (key === "p" && commandSetContains(commandSet, "p")) {
    return {
      type: "command",
      command: { kind: "put", before: false, register: pending.register },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (key === "P" && commandSetContains(commandSet, "P")) {
    return {
      type: "command",
      command: { kind: "put", before: true, register: pending.register },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (keystroke.ctrl && key === "v" && commandSetContains(commandSet, "<C-v>")) {
    return {
      type: "command",
      command: { kind: "enter-visual", variant: "visual-block" },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (!keystroke.ctrl && key === "v" && commandSetContains(commandSet, "v")) {
    return {
      type: "command",
      command: { kind: "enter-visual", variant: "visual" },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (key === "V" && commandSetContains(commandSet, "V")) {
    return {
      type: "command",
      command: { kind: "enter-visual", variant: "visual-line" },
      pending: EMPTY_PENDING,
      rawKeys: [...pending.rawKeys, keystroke],
    };
  }

  if (key === "m" && commandSetContains(commandSet, "m")) {
    return {
      type: "pending",
      pending: { ...pending, awaiting: "mark-set", rawKeys: [...pending.rawKeys, keystroke] },
    };
  }

  if (key === "`" && commandSetContains(commandSet, "`")) {
    return {
      type: "pending",
      pending: { ...pending, awaiting: "mark-jump", rawKeys: [...pending.rawKeys, keystroke] },
    };
  }

  if (key === "q" && commandSetContains(commandSet, "q")) {
    return {
      type: "pending",
      pending: {
        ...pending,
        awaiting: "macro-record-name",
        rawKeys: [...pending.rawKeys, keystroke],
      },
    };
  }

  if (key === "@" && commandSetContains(commandSet, "@")) {
    return {
      type: "pending",
      pending: {
        ...pending,
        awaiting: "macro-play-name",
        rawKeys: [...pending.rawKeys, keystroke],
      },
    };
  }

  if (key === "g") {
    if (commandSetContains(commandSet, "gg")) {
      return {
        type: "pending",
        pending: { ...pending, awaiting: "g", rawKeys: [...pending.rawKeys, keystroke] },
      };
    }
    return unlearned();
  }

  if (isSingleKeyMotion(key) && commandSetContains(commandSet, key)) {
    const rawKeys = [...pending.rawKeys, keystroke];
    return confirmMotionOrOperator(pending, key, pending.count, undefined, rawKeys);
  }

  return unlearned();
}
